import { api } from './api';

/**
 * Someone work can be assigned to. Deliberately not a `Member`: an assignee is
 * not a user of the system, has a required name and an optional email, and
 * never has a picture. `Member` stays the shape of whoever *did* something --
 * created a task, wrote a comment, logged time.
 *
 * Mirrors the server's `toAssigneeResponse`. Hand-kept in sync; there is no
 * shared types package.
 */
export type Assignee = {
  id: string;
  name: string;
  email: string | null;
  role: string | null;
  /** Archived people leave the pickers but stay on the tasks they hold. */
  archived: boolean;
  archivedAt: string | null;
  /**
   * Starred by *you* as the person your new tasks belong to by default. Whoever
   * else has starred whom is not reported: the server answers this one per
   * viewer, so at most one assignee in a response ever has it set.
   */
  isDefault: boolean;
  /** Only on the single-assignee and list endpoints, not when embedded. */
  notes?: string | null;
  taskCount?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type AssigneeInput = {
  name: string;
  email: string | null;
  role: string | null;
  notes: string | null;
};

export const listAssignees = (options: { includeArchived?: boolean } = {}, signal?: AbortSignal) =>
  api.get<{ assignees: Assignee[] }>(
    `/api/assignees${options.includeArchived ? '?includeArchived=true' : ''}`,
    signal,
  );

export const getAssignee = (id: string, signal?: AbortSignal) =>
  api.get<{ assignee: Assignee }>(`/api/assignees/${id}`, signal);

export const createAssignee = (input: AssigneeInput) =>
  api.post<{ assignee: Assignee }>('/api/assignees', input);

export const updateAssignee = (
  id: string,
  input: Partial<AssigneeInput & { archived: boolean; isDefault: boolean }>,
) =>
  api.patch<{ assignee: Assignee }>(`/api/assignees/${id}`, input);

export const deleteAssignee = (id: string) =>
  api.del<{ ok: true; affectedTaskCount: number }>(`/api/assignees/${id}`);

/**
 * The options a picker should offer: everyone active, plus anyone currently
 * selected even if archived.
 *
 * Without that second half the archived person is not among the `<option>`s, the
 * `<select>` falls back to its empty value, and the next unrelated edit silently
 * clears the assignment.
 */
export function pickerOptions(assignees: Assignee[], selectedIds: string[]): Assignee[] {
  return assignees.filter(
    (assignee) => !assignee.archived || selectedIds.includes(assignee.id),
  );
}

/**
 * Who a new task belongs to before anybody picks -- the starred person, as long
 * as they are still around.
 *
 * The server applies the same rule to any create that omits an owner (see
 * `defaultOwnerFor`); this is what lets the form show the answer up front rather
 * than filling it in after the fact.
 */
export function defaultOwnerId(assignees: Assignee[]): string | null {
  return assignees.find((assignee) => assignee.isDefault && !assignee.archived)?.id ?? null;
}

/**
 * A label that identifies one person on its own.
 *
 * A `<datalist>` hands back the option's *text*, not its value, so two people
 * called Ada would be indistinguishable on the way back. Email disambiguates
 * when there is one, and a short id when there is not -- and only when there is
 * actually a clash, so the common case stays clean.
 */
export function assigneeLabel(assignee: Assignee, all: Assignee[]): string {
  const clashes = all.filter((other) => other.name === assignee.name).length > 1;
  if (!clashes) return assignee.name;
  return assignee.email
    ? `${assignee.name} (${assignee.email})`
    : `${assignee.name} (#${assignee.id.slice(0, 4)})`;
}
